import { useEffect, useRef, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CARD, CARD2, CARD3, BD, TX, TX2, GR, BL, YL, RD } from "../../utils/constants";
import StockCard from "./StockCard";
import StocksTable from "./StocksTable";
import SubscriptionGate from "../Subscription/SubscriptionGate";
import StockSearch from "./StockSearch";

const TICKER_SYMBOLS = [
  "AAPL","NVDA","TSLA","MSFT","AMZN","META","GOOG","NFLX","SPY","QQQ",
  "AMD","INTC","SOFI","PLTR","COIN","BAC","JPM","GS","XOM","CVX",
  "SHOP","SQ","PYPL","UBER","SNOW","CRM","ORCL","ADBE","ARM","MSTR",
  "BABA","NIO","RIVN","LCID","WOLF","SMCI","MRVL","ANET","PANW","CRWD",
];

// ── Animated scanner header ───────────────────────────────────────────────────
function ScannerHeader({ marketOpen, scanned, scannedStocks }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: -14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      style={{ marginBottom: 16, position: "relative" }}
    >
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        {/* Title */}
        <div>
          <motion.div
            style={{
              fontFamily: "'Space Grotesk',sans-serif",
              fontWeight: 900, fontSize: 24, letterSpacing: 1,
              background: `linear-gradient(90deg, ${GR} 0%, ${BL} 50%, ${GR} 100%)`,
              backgroundSize: "200% 100%",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
            animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
            transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
          >
            MARKET SCANNER
          </motion.div>
          <div style={{ fontSize: 8, color: TX2, letterSpacing: 3, marginTop: 3 }}>
            REAL-TIME SWING ANALYSIS ENGINE · {scanned ? `${scannedStocks.length} STOCKS` : "READY"}
          </div>
        </div>

        {/* Right HUD */}
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          {/* Live clock */}
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 8, color: TX2, letterSpacing: 2, marginBottom: 3 }}>LOCAL TIME</div>
            <div style={{
              fontFamily: "'IBM Plex Mono',monospace",
              fontSize: 20, fontWeight: 700,
              color: GR, letterSpacing: 3,
              textShadow: `0 0 14px ${GR}70`,
            }}>
              {time.toLocaleTimeString("en-US", { hour12: false })}
            </div>
          </div>

          {/* Market status */}
          <div style={{ background: CARD, border: `1px solid ${marketOpen ? GR + "35" : RD + "35"}`, borderRadius: 8, padding: "8px 14px", textAlign: "center" }}>
            <motion.div
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.8, repeat: Infinity }}
              style={{ width: 7, height: 7, borderRadius: "50%", background: marketOpen ? GR : RD, boxShadow: `0 0 10px ${marketOpen ? GR : RD}`, margin: "0 auto 5px" }}
            />
            <div style={{ fontSize: 8, color: marketOpen ? GR : RD, letterSpacing: 2, fontWeight: 700 }}>
              {marketOpen ? "LIVE" : "CLOSED"}
            </div>
          </div>
        </div>
      </div>

      {/* Animated scan line under header */}
      <div style={{ position: "relative", height: 1, background: BD, marginTop: 13, overflow: "hidden" }}>
        <motion.div
          style={{
            position: "absolute", top: 0, height: "100%", width: "25%",
            background: `linear-gradient(90deg, transparent, ${GR}90, ${BL}60, transparent)`,
          }}
          animate={{ x: ["-25%", "125%"] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "linear" }}
        />
      </div>

      {/* HUD corner dots */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <motion.div
            key={i}
            animate={{ opacity: [0.15, 0.7, 0.15] }}
            transition={{ duration: 2, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
            style={{ width: 3, height: 3, borderRadius: "50%", background: GR }}
          />
        ))}
      </div>
    </motion.div>
  );
}

// ── Radar / sonar ────────────────────────────────────────────────────────────
function RadarScan() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const SIZE = 150;
    canvas.width = canvas.height = SIZE;
    const ctx = canvas.getContext("2d");
    const cx = SIZE / 2, cy = SIZE / 2, R = SIZE / 2 - 6;

    // Random blips scattered around
    const blips = Array.from({ length: 14 }, () => ({
      angle: Math.random() * Math.PI * 2,
      dist: (0.25 + Math.random() * 0.7) * R,
      alpha: 0,
    }));

    let sweep = -Math.PI / 2; // start at top

    const draw = () => {
      ctx.clearRect(0, 0, SIZE, SIZE);

      // Dark background fill
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(5,8,20,0.82)";
      ctx.fill();

      // Concentric rings
      [0.28, 0.52, 0.76, 1].forEach((f) => {
        ctx.beginPath();
        ctx.arc(cx, cy, R * f, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(0,230,118,0.12)";
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      // Cross-hair lines
      ctx.strokeStyle = "rgba(56,189,248,0.10)";
      ctx.lineWidth = 0.5;
      [0, Math.PI / 2].forEach((a) => {
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * R, cy + Math.sin(a) * R);
        ctx.lineTo(cx - Math.cos(a) * R, cy - Math.sin(a) * R);
        ctx.stroke();
      });

      // Sweep trail (fade sector behind the arm)
      const trailLen = Math.PI * 0.6;
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
      grad.addColorStop(0, "rgba(0,230,118,0.10)");
      grad.addColorStop(1, "rgba(0,230,118,0.01)");
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R, sweep - trailLen, sweep);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();

      // Sweep arm
      const ax = cx + Math.cos(sweep) * R;
      const ay = cy + Math.sin(sweep) * R;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(ax, ay);
      ctx.strokeStyle = GR;
      ctx.lineWidth = 1.5;
      ctx.shadowBlur = 10;
      ctx.shadowColor = GR;
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Leading dot on arm tip
      ctx.beginPath();
      ctx.arc(ax, ay, 3, 0, Math.PI * 2);
      ctx.fillStyle = "#fff";
      ctx.shadowBlur = 12;
      ctx.shadowColor = GR;
      ctx.fill();
      ctx.shadowBlur = 0;

      // Centre dot
      ctx.beginPath();
      ctx.arc(cx, cy, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(0,230,118,0.6)";
      ctx.fill();

      // Blips — activate when sweep passes over them, then fade
      blips.forEach((b) => {
        const norm = (a) => ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        const diff = (norm(sweep) - norm(b.angle) + Math.PI * 2) % (Math.PI * 2);
        if (diff < 0.07) b.alpha = 1;

        if (b.alpha > 0.01) {
          const bx = cx + Math.cos(b.angle) * b.dist;
          const by = cy + Math.sin(b.angle) * b.dist;
          const r = 2 + b.alpha * 1.5;
          ctx.beginPath();
          ctx.arc(bx, by, r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(0,230,118,${b.alpha * 0.9})`;
          ctx.shadowBlur = 10 * b.alpha;
          ctx.shadowColor = GR;
          ctx.fill();
          ctx.shadowBlur = 0;
          b.alpha -= 0.007;
        }
      });

      // Outer border ring
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(0,230,118,0.22)";
      ctx.lineWidth = 1;
      ctx.stroke();

      sweep += 0.022;
    };

    const id = setInterval(draw, 16);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <canvas ref={canvasRef} width={150} height={150} style={{ display: "block", borderRadius: "50%" }} />
      <div style={{ fontSize: 7, color: `${GR}60`, letterSpacing: 3 }}>RADAR SWEEP</div>
    </div>
  );
}

// ── Matrix rain ───────────────────────────────────────────────────────────────
function MatrixRain() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const setSize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    setSize();
    const chars = ["AAPL","NVDA","TSLA","META","SPY","QQQ","AMD","94","42","+2.1","-0.8","100","WATCH","EDGE","RISK","SCAN","0101","1010","73","88"];
    const colW = 22;
    const cols = Math.floor(canvas.width / colW);
    const drops = Array.from({ length: cols }, () => Math.random() * -40);
    const ctx = canvas.getContext("2d");
    const draw = () => {
      ctx.fillStyle = "rgba(9,12,22,0.07)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      drops.forEach((y, i) => {
        const ch = chars[Math.floor(Math.random() * chars.length)];
        const alpha = Math.random() * 0.13 + 0.04;
        ctx.fillStyle = `rgba(0,230,118,${alpha})`;
        ctx.font = `10px 'IBM Plex Mono',monospace`;
        ctx.fillText(ch, i * colW, y * 15);
        if (y * 15 > canvas.height && Math.random() > 0.975) drops[i] = 0;
        drops[i] += 0.35;
      });
    };
    const id = setInterval(draw, 55);
    return () => clearInterval(id);
  }, []);
  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", borderRadius: 12 }} />;
}

// ── Neon progress bar ─────────────────────────────────────────────────────────
function NeonProgressBar({ progress }) {
  return (
    <div style={{ position: "relative", height: 3, background: CARD3, borderRadius: 4 }}>
      <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: `${progress}%`, background: `linear-gradient(90deg, ${GR}60, ${GR})`, borderRadius: 4, boxShadow: `0 0 8px ${GR}, 0 0 20px ${GR}50`, transition: "width 0.12s ease-out", overflow: "hidden" }}>
        <motion.div
          style={{ position: "absolute", inset: 0, width: "35%", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.38), transparent)" }}
          animate={{ x: ["-100%", "450%"] }}
          transition={{ duration: 1.3, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
      {progress > 1 && progress < 99 && (
        <div style={{ position: "absolute", top: "50%", left: `${progress}%`, width: 8, height: 8, borderRadius: "50%", background: "#fff", boxShadow: `0 0 8px ${GR}, 0 0 18px ${GR}, 0 0 3px #fff`, transform: "translate(-50%, -50%)", transition: "left 0.12s ease-out", zIndex: 2 }} />
      )}
    </div>
  );
}

// ── Live ticker ───────────────────────────────────────────────────────────────
function ScanTicker() {
  const shuffled = useMemo(() => [...TICKER_SYMBOLS].sort(() => Math.random() - 0.5), []);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => { setIdx((i) => (i + 1) % shuffled.length); setVisible(true); }, 180);
    }, 650);
    return () => clearInterval(id);
  }, [shuffled]);
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 8, color: TX2, letterSpacing: 4, marginBottom: 10 }}>SCANNING</div>
      <AnimatePresence mode="wait">
        {visible && (
          <motion.div key={idx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.16 }}
            style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 34, fontWeight: 700, color: GR, letterSpacing: 4, textShadow: `0 0 18px ${GR}90, 0 0 40px ${GR}40` }}>
            {shuffled[idx]}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Count-up with landing flash ───────────────────────────────────────────────
function CountUp({ target, color, duration = 700 }) {
  const [count, setCount] = useState(0);
  const [flash, setFlash] = useState(false);
  const timerRef = useRef(null);
  const flashRef = useRef(null);
  useEffect(() => {
    clearInterval(timerRef.current); clearTimeout(flashRef.current);
    setCount(0); setFlash(false);
    if (!target) return;
    let val = 0;
    const increment = target / (duration / 16);
    timerRef.current = setInterval(() => {
      val += increment;
      if (val >= target) { setCount(target); setFlash(true); clearInterval(timerRef.current); flashRef.current = setTimeout(() => setFlash(false), 500); }
      else setCount(Math.floor(val));
    }, 16);
    return () => { clearInterval(timerRef.current); clearTimeout(flashRef.current); };
  }, [target, duration]);
  return <span style={{ color, textShadow: flash ? `0 0 16px ${color}, 0 0 32px ${color}70` : "none", transition: "text-shadow 0.35s ease-out" }}>{count}</span>;
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ScannerTab({
  loading, scanPhase, scanned, scannedStocks, lastScan, scanError,
  topN, topPicks, projGain,
  watchlist, onToggleWL,
  sector, setSector, search, setSearch, filtered,
  onScan, onStockClick, onSearchStock,
  marketOpen,
  isPro, subLoading, paymentUrl, onSubscribe, onActivateDemo,
}) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 760;
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(null);
  useEffect(() => {
    clearInterval(progressRef.current);
    if (loading) {
      let p = 0; setProgress(0);
      progressRef.current = setInterval(() => { p += (87 - p) * 0.017; setProgress(p); }, 100);
    } else { setProgress(0); }
    return () => clearInterval(progressRef.current);
  }, [loading]);

  const cornerDelays = useMemo(() => [0, 0.6, 1.1, 0.3], []);

  return (
    <>
      {/* ── Animated header ── */}
      <ScannerHeader marketOpen={marketOpen} scanned={scanned} scannedStocks={scannedStocks} />

      {isPro && <StockSearch onSelect={onSearchStock} />}

      {!isPro && !subLoading && (
        <SubscriptionGate
          title="Subscribe to unlock Pro signals"
          subtitle="Market scanning, ranked research candidates, score breakdowns, risk/reward levels, and signal history are included in Trade Scanner Starter."
          paymentUrl={paymentUrl}
          onSubscribe={onSubscribe}
          onActivateDemo={onActivateDemo}
        />
      )}

      {/* ── Scan control card ── */}
      <div className="ts-panel" style={{ background: CARD, border: `1px solid ${loading ? GR + "45" : BD}`, borderRadius: 12, padding: "14px 18px", marginBottom: 12, transition: "border-color 0.5s", boxShadow: loading ? `0 0 24px ${GR}12` : "none", opacity: (isPro || subLoading) ? 1 : 0.68 }}>
        <div style={{ display: "flex", alignItems: isMobile ? "stretch" : "center", gap: 14, flexWrap: "wrap", flexDirection: isMobile ? "column" : "row" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, fontSize: 15, color: TX, marginBottom: 2 }}>
              {loading ? <span style={{ color: TX2, fontSize: 12, letterSpacing: 0.5 }}>{scanPhase}</span>
                : scanned ? `${scannedStocks.length} stocks ranked` : "Ready to scan"}
            </div>
            <div style={{ fontSize: 9, color: TX2 }}>swing focus{lastScan ? ` · updated ${lastScan.toLocaleTimeString()}` : ""}</div>
            {scanError && <div style={{ fontSize: 9, color: RD, marginTop: 4 }}>{scanError}</div>}
          </div>
          <div style={{ display: "flex", gap: 7, flexShrink: 0, width: isMobile ? "100%" : "auto" }}>
            <button className="btn" onClick={onScan} disabled={loading}
              style={{ width: isMobile ? "100%" : "auto", background: loading ? CARD2 : `linear-gradient(135deg,${GR},${BL})`, borderRadius: 8, padding: "9px 22px", color: loading ? TX2 : "#000", fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: loading ? "not-allowed" : "pointer", fontFamily: "'Space Grotesk',sans-serif" }}>
              {loading ? "SCANNING..." : (isPro || subLoading) ? "SCAN MARKET" : "UNLOCK PRO"}
            </button>
          </div>
        </div>
        {loading && <div style={{ marginTop: 14 }}><NeonProgressBar progress={progress} /></div>}
      </div>

      {/* ── Scan overlay ── */}
      <AnimatePresence>
        {loading && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}
            className="ts-panel"
            style={{ position: "relative", overflow: "hidden", background: CARD, border: `1px solid ${GR}28`, borderRadius: 12, padding: "36px 24px 32px", marginBottom: 12, minHeight: 210, boxShadow: `0 0 50px ${GR}08, inset 0 0 80px rgba(0,230,118,0.025)` }}>
            <MatrixRain />
            {[{ top: 10, left: 10, borderTop: `1px solid ${GR}30`, borderLeft: `1px solid ${GR}30` }, { top: 10, right: 10, borderTop: `1px solid ${GR}30`, borderRight: `1px solid ${GR}30` }, { bottom: 10, left: 10, borderBottom: `1px solid ${GR}30`, borderLeft: `1px solid ${GR}30` }, { bottom: 10, right: 10, borderBottom: `1px solid ${GR}30`, borderRight: `1px solid ${GR}30` }].map((s, i) => (
              <div key={i} style={{ position: "absolute", width: 16, height: 16, ...s }} />
            ))}
            <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 0, justifyContent: "space-between" }}>
              {/* Left: ticker + analysis labels */}
              <div style={{ flex: 1 }}>
                <ScanTicker />
                <div style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 18 }}>
                  {["MOMENTUM", "TREND", "VOLUME", "SIGNALS"].map((label, i) => (
                    <motion.div key={label} animate={{ opacity: [0.25, 0.8, 0.25] }} transition={{ duration: 2, repeat: Infinity, delay: cornerDelays[i], ease: "easeInOut" }} style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 7, color: `${GR}70`, letterSpacing: 2, marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 9, color: `${GR}50`, fontFamily: "'IBM Plex Mono'" }}>████</div>
                    </motion.div>
                  ))}
                </div>
              </div>
              {/* Right: radar */}
              <div style={{ flexShrink: 0, paddingLeft: 24 }}>
                <RadarScan />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Results ── */}
      {isPro && scanned && !loading && scannedStocks.length > 0 && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 7, marginBottom: 12 }}>
            {[
              { l: "SCANNED",      v: scannedStocks.length,                                             c: TX },
              { l: "PRO SIGNALS",  v: scannedStocks.filter((s) => s.score >= 60).length,                c: GR },
              { l: "BULLISH TMRW", v: scannedStocks.filter((s) => s.tomorrowBias === "BULLISH").length, c: YL },
              { l: "LOW SCORE",    v: scannedStocks.filter((s) => s.score < 30).length,                 c: RD },
            ].map((s, i) => (
              <motion.div key={s.l} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.06, ease: "easeOut" }}
                className="ts-panel"
                style={{ background: CARD, border: `1px solid ${BD}`, borderRadius: 9, padding: "10px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Space Grotesk',sans-serif" }}>
                  <CountUp target={s.v} color={s.c} duration={550 + i * 80} />
                </div>
                <div style={{ fontSize: 7, color: TX2, letterSpacing: 1, marginTop: 2 }}>{s.l}</div>
              </motion.div>
            ))}
          </div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.28 }}
            className="ts-panel"
            style={{ background: CARD, border: `1px solid ${projGain >= 0 ? GR + "30" : RD + "30"}`, borderRadius: 10, padding: "11px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 7, color: TX2, letterSpacing: 2, marginBottom: 1 }}>TODAY AVG — TOP {topN}</div>
              <div style={{ fontSize: 24, fontWeight: 800, fontFamily: "'Space Grotesk',sans-serif", color: projGain >= 0 ? GR : RD }}>{projGain >= 0 ? "+" : ""}{projGain.toFixed(2)}%</div>
            </div>
          </motion.div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 8, color: TX2, letterSpacing: 3, marginBottom: 8 }}>TOP {topN} PICKS</div>
            {topPicks.map((stock, i) => (
              <motion.div key={stock.symbol} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.28, delay: 0.35 + i * 0.07, ease: "easeOut" }}>
                <StockCard stock={stock} rank={i + 1} isWatchlisted={watchlist.includes(stock.symbol)} onToggleWL={onToggleWL} onStockClick={onStockClick} />
              </motion.div>
            ))}
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.5 }}>
            <StocksTable filtered={filtered} sector={sector} setSector={setSector} search={search} setSearch={setSearch} watchlist={watchlist} onToggleWL={onToggleWL} onStockClick={onStockClick} />
          </motion.div>
        </>
      )}
    </>
  );
}
